import cloneDeep from 'lodash/cloneDeep';

import { EntityType, ContentRestrictionType, PermissionType } from '../types';
import type {
	userEntityShape,
	groupEntityShape,
} from '../RestrictionsDialog/restrictions-data-transform-helpers';
import { SitePermissionType } from '../RestrictionsDialog/__types__/RestrictionsDialogQuery';

import type {
	RestrictionsRESTQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_update_ContentRestriction_restrictions_SubjectsByType_userWithRestrictions_PaginatedUserWithRestrictions_nodes_UserWithRestrictions as GraphQLUser,
	RestrictionsRESTQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_update_ContentRestriction_restrictions_SubjectsByType_groupWithRestrictions_PaginatedGroupWithRestrictions_nodes_GroupWithRestrictions as GraphQLGroup,
	RestrictionsRESTQuery as RestrictionsRESTQueryType,
} from './__types__/RestrictionsRESTQuery';

export type CurrentUser = {
	id: string;
	profilePicture: {
		path: string;
	};
	displayName: string;
};

// These types come from the Confluence REST API:
// https://developer.atlassian.com/cloud/confluence/rest/v1/api-group-content-restrictions/#api-wiki-rest-api-content-id-restriction-get
type OperationType =
	| 'administer'
	| 'copy'
	| 'create'
	| 'delete'
	| 'export'
	| 'move'
	| 'purge'
	| 'purge_version'
	| 'read'
	| 'restore'
	| 'update'
	| 'use';
export type RESTUser = {
	type: 'known' | 'unknown' | 'anonymous' | 'user';
	accountId: string;
	publicName: string;
	profilePicture: {
		path: string;
	};
	displayName: string;
	email?: string;
	hasSpaceViewPermission?: boolean;
	hasSpaceEditPermission?: boolean;
	isExternalCollaborator: boolean;
};
export type RESTGroup = {
	type: 'group';
	name: string;
	id: string;
};
export type ContentRestriction = {
	operation: OperationType;
	restrictions: {
		user: {
			results: RESTUser[];
		};
		group: {
			results: RESTGroup[];
		};
	};
};
export type RestrictionsRESTObj = ContentRestriction[];

export type RestrictionsRESTObjForContentApi = {
	[key in OperationType]: ContentRestriction;
};

class InvalidRestrictionsObjectError extends Error {
	constructor() {
		super(
			"Invalid restrictions object provided.  Restrictions objects must contain a 'read' ContentRestriction and an 'update' ContentRestriction.",
		);
	}
}
class InvalidGraphQLRestrictionsError extends Error {
	constructor() {
		super('Invalid restrictions were fetched from GraphQL.');
	}
}

const restrictionIsEmpty = (contentRestriction: ContentRestriction): boolean => {
	return (
		contentRestriction.restrictions.user.results.length === 0 &&
		contentRestriction.restrictions.group.results.length === 0
	);
};
export const getRestrictionsTypeFromRESTObj = (
	restObj: RestrictionsRESTObj,
): ContentRestrictionType => {
	const readRestriction = restObj.find((obj) => obj.operation === 'read');
	const updateRestriction = restObj.find((obj) => obj.operation === 'update');
	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidRestrictionsObjectError();
	}
	if (restrictionIsEmpty(readRestriction) && restrictionIsEmpty(updateRestriction)) {
		return ContentRestrictionType.NO_RESTRICTIONS;
	} else if (restrictionIsEmpty(readRestriction)) {
		return ContentRestrictionType.RESTRICT_EDIT;
	} else {
		return ContentRestrictionType.RESTRICT_VIEW;
	}
};
export const applyRestrictionsTypeToRESTObj = (
	restObj: RestrictionsRESTObj,
	newRestrictionType: ContentRestrictionType,
	currentUser: CurrentUser,
): RestrictionsRESTObj => {
	// This function applies a restrictionType to a RestrictionsRESTObj.
	// Some of the logic here is duplicated by `getPermissionsGroupsBasedOnRestrictionsType`
	// in RestrictionsDialog/restrictions-data-transform-helpers.tsx
	// Be sure that any changes you make to how this logic works are also made in that file.
	const oldRestrictionType = getRestrictionsTypeFromRESTObj(restObj);
	const currentUserObj = {
		type: 'known' as 'known', // The `as "known"` is needed to satisfy a Typescript error, since `type` gets assigned the type "string"
		accountId: currentUser.id,
		profilePicture: {
			path: currentUser.profilePicture.path,
		},
		displayName: currentUser.displayName,
		publicName: currentUser.displayName,
		isExternalCollaborator: false,
	};

	// First make a deep copy of the restObj.
	const restObjCopy = cloneDeep(restObj);
	const readRestriction = restObjCopy.find((obj) => obj.operation === 'read');
	const updateRestriction = restObjCopy.find((obj) => obj.operation === 'update');
	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidRestrictionsObjectError();
	}

	if (newRestrictionType === ContentRestrictionType.NO_RESTRICTIONS) {
		// Empty out update and read arrays.
		readRestriction.restrictions.user.results = [];
		readRestriction.restrictions.group.results = [];
		updateRestriction.restrictions.user.results = [];
		updateRestriction.restrictions.group.results = [];
		return restObjCopy;
	} else if (newRestrictionType === ContentRestrictionType.RESTRICT_EDIT) {
		if (oldRestrictionType === ContentRestrictionType.RESTRICT_VIEW) {
			readRestriction.restrictions.user.results = [];
			readRestriction.restrictions.group.results = [];
			return restObjCopy;
		} else if (oldRestrictionType === ContentRestrictionType.RESTRICT_EDIT) {
			return restObjCopy;
		} else {
			// oldRestrictionType === ContentRestrictionType.NO_RESTRICTIONS
			updateRestriction.restrictions.user.results.push(currentUserObj);
			return restObjCopy;
		}
	} else {
		// newRestrictionType === ContentRestrictionType.RESTRICT_VIEW
		if (oldRestrictionType === ContentRestrictionType.RESTRICT_VIEW) {
			return restObjCopy;
		} else if (oldRestrictionType === ContentRestrictionType.RESTRICT_EDIT) {
			readRestriction.restrictions.user.results.push(currentUserObj);
			return restObjCopy;
		} else {
			// oldRestrictionType === ContentRestrictionType.NO_RESTRICTIONS
			readRestriction.restrictions.user.results.push(currentUserObj);
			updateRestriction.restrictions.user.results.push(currentUserObj);
			return restObjCopy;
		}
	}

	// Permissions levels:
	// NO_RESTRICTIONS
	// RESTRICT_EDIT
	// RESTRICT_VIEW
	//
	// To move upwards on that list, just empty out the restrictions arrays.
	// To move downwards on that list, you have to add the current user into the correct restrictions segment.
};

const convertRESTUser = (restUser: RESTUser): userEntityShape => {
	return {
		key: `USER-${restUser.accountId}`,
		entityType: EntityType.USER,
		permissions: [],
		displayName: restUser.displayName,
		accountId: restUser.accountId,
		profilePicture: {
			path: restUser.profilePicture.path,
		},
		hasSpaceViewPermission: restUser?.hasSpaceViewPermission ?? true,
		hasSpaceEditPermission: restUser?.hasSpaceEditPermission ?? true,
		permissionType: restUser.isExternalCollaborator
			? SitePermissionType.EXTERNAL
			: SitePermissionType.INTERNAL,
	};
};
const convertRESTGroup = (restGroup: RESTGroup): groupEntityShape => {
	return {
		key: `GROUP-${restGroup.id}`,
		entityType: EntityType.GROUP,
		permissions: [],
		name: restGroup.name,
		permissionType: SitePermissionType.INTERNAL,
	};
};
export const getUsersAndGroupsFromRESTObj = (
	restObj: RestrictionsRESTObj,
): Array<userEntityShape | groupEntityShape> => {
	const readRestriction = restObj.find((obj) => obj.operation === 'read');
	const updateRestriction = restObj.find((obj) => obj.operation === 'update');
	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidRestrictionsObjectError();
	}

	// TODO It may be possible to re-use some of the logic in
	// restrictions-data-transform-helpers.tsx here.

	// First, make a map from accountIds or group ids to their corresponding objects.
	const userGroupMap = new Map<string, userEntityShape | groupEntityShape>();
	for (const user of readRestriction.restrictions.user.results) {
		if (!userGroupMap.has(user.accountId)) {
			userGroupMap.set(user.accountId, convertRESTUser(user));
		}
		userGroupMap.get(user.accountId)!.permissions.push(PermissionType.VIEW);
	}
	for (const group of readRestriction.restrictions.group.results) {
		if (!userGroupMap.has(group.id)) {
			userGroupMap.set(group.id, convertRESTGroup(group));
		}
		userGroupMap.get(group.id)!.permissions.push(PermissionType.VIEW);
	}
	for (const user of updateRestriction.restrictions.user.results) {
		if (!userGroupMap.has(user.accountId)) {
			userGroupMap.set(user.accountId, convertRESTUser(user));
		}
		userGroupMap.get(user.accountId)!.permissions.push(PermissionType.EDIT);
	}
	for (const group of updateRestriction.restrictions.group.results) {
		if (!userGroupMap.has(group.id)) {
			userGroupMap.set(group.id, convertRESTGroup(group));
		}
		userGroupMap.get(group.id)!.permissions.push(PermissionType.EDIT);
	}

	// Return an array out of all the values from the map.
	// This matches the format of the usersAndGroups object.
	return Array.from(userGroupMap).map(([, value]) => value);
};

// These 2 transform functions are similar to the ones
// in restrictions-data-transform-helpers.tsx, but not exactly the same
// because the GraphQL format is slightly different from
// the format the RestrictionsDialog uses internally.
export const transformUserToREST = (user: GraphQLUser): RESTUser => {
	return {
		type: 'known',
		accountId: user?.accountId ?? '',
		publicName: user?.publicName ?? '',
		profilePicture: {
			path: user?.profilePicture?.path ?? '',
		},
		email: user?.email ?? '',
		displayName: user?.displayName ?? '',
		hasSpaceViewPermission: user?.hasSpaceViewPermission ?? true,
		hasSpaceEditPermission: user?.hasSpaceEditPermission ?? true,
		isExternalCollaborator: user.permissionType === SitePermissionType.EXTERNAL,
	};
};

export const transformGroupToREST = (group: GraphQLGroup): RESTGroup => {
	return {
		type: 'group',
		name: group?.name ?? '',
		id: group?.id ?? '',
	};
};

const isNotNull = <T>(value: T | null): value is T => {
	return value !== null;
};

export const transformGraphQLQueryToRESTObj = (
	queryData: RestrictionsRESTQueryType,
): RestrictionsRESTObj => {
	const readRestriction = queryData?.content?.nodes?.[0]?.restrictions?.read?.restrictions;
	const updateRestriction = queryData?.content?.nodes?.[0]?.restrictions?.update?.restrictions;
	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidGraphQLRestrictionsError();
	}

	const readObj: ContentRestriction = {
		operation: 'read',
		restrictions: {
			user: { results: [] },
			group: { results: [] },
		},
	};
	const updateObj: ContentRestriction = {
		operation: 'update',
		restrictions: {
			user: { results: [] },
			group: { results: [] },
		},
	};
	readObj.restrictions.user.results = (readRestriction?.userWithRestrictions?.nodes ?? [])
		.filter(isNotNull)
		.map(transformUserToREST);
	readObj.restrictions.group.results = (readRestriction?.groupWithRestrictions?.nodes ?? [])
		.filter(isNotNull)
		.map(transformGroupToREST);
	updateObj.restrictions.user.results = (updateRestriction?.userWithRestrictions?.nodes ?? [])
		.filter(isNotNull)
		.map(transformUserToREST);
	updateObj.restrictions.group.results = (updateRestriction?.groupWithRestrictions?.nodes ?? [])
		.filter(isNotNull)
		.map(transformGroupToREST);

	return [readObj, updateObj];
};

// This function returns true if only one user has access
// to read and update the provided restrictions object.
export const restrictionsArePrivateToYouOnly = (restObj: RestrictionsRESTObj): boolean => {
	const readRestriction = restObj.find((obj) => obj.operation === 'read');
	const updateRestriction = restObj.find((obj) => obj.operation === 'update');
	if (readRestriction === undefined || updateRestriction === undefined) {
		throw new InvalidRestrictionsObjectError();
	}

	const onlyOneUserCanRead = readRestriction.restrictions.user.results.length === 1;
	const noGroupsCanRead = readRestriction.restrictions.group.results.length === 0;
	const onlyOneUserCanUpdate = updateRestriction.restrictions.user.results.length === 1;
	const noGroupsCanUpdate = updateRestriction.restrictions.group.results.length === 0;

	return onlyOneUserCanRead && noGroupsCanRead && onlyOneUserCanUpdate && noGroupsCanUpdate;
};
